import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { createPortal } from 'react-dom'
import { alpha, Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import AppImage from '@/components/AppImage'
import { serverUrl } from '@/utils/app-helper'
import { formatMoney } from '@/utils/locale-format'
import { PriceDisplay } from '@/components/pricing/PriceDisplay'
import type { MenuItemInterface } from '@/models/menu-item'
import type { CartModifierGroupStored } from '@/store/cart/cart.types'
import type { ModifierGroupV2, ModifierGroupItemV2 } from '@/models/menu-item-v2'
import { designTokens, fontWeight } from '@/theme'
import { getDesignTokens } from '@/theme/designTokens'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { useCartStore } from '@/store/cart/cart-store'
import { readSafeCartItems, selectCartItems } from '@/store/cart/cart.selectors'
import { useCartLockRecovery } from '@/context/cart-lock-recovery-context'
import { cartLineToSelectedModifiers } from '@/store/cart/cart-line-adapters'
import { updateMenuItems } from '@/slices/menu-items-slice'
import { CrossIcon } from '@/components/icons'
import PrimaryButton from '@/components/common/PrimaryButton'
import TagChip from '@/components/common/TagChip'
import { computeLineTotalForCartItemBase } from '@/domain/cart/cart.pricing'
import { sumMenuItemQuantity } from '@/domain/cart/cart.validation'
import { MAX_MENU_ITEM_QUANTITY } from '@/domain/cart/cart.constants'
import { ExpandableDescription } from '@/components/menu/ExpandableDescription'
import { MenuItemMetadata } from '@/components/menu/MenuItemMetadata'
import { ScrollFadeContainer } from '@/components/menu/ScrollFadeContainer'
import {
  getMenuQuantityStepperButtonSx,
  getMenuQuantityStepperQtySx,
  getMenuQuantityStepperShellSx,
} from '@/components/menu/menuQuantityStepper.styles'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'
import { useTenantBranding } from '@/hooks/use-tenant-branding'
import { SheetOverlay } from '@/components/cart/CartDrawer'
import { REACT_STABLE_KEY_TYPE, buildStableKey, normalizeNumber } from '@/utils/react-key.util'
import {
  tenantActionCtaSx,
} from '@/theme/tenant-cta-sx'

type Props = {
  item: MenuItemInterface
  isOpen: boolean
  onClose: () => void
  /**
   * When set, modal opens in quantity-only Edit mode for this cart line.
   * Browsing / Add flows omit this (or pass null).
   */
  editClientItemId?: string | null
  sheetSnapPoints?: Array<string | number>
  sheetInitialSnap?: number
}

/** Combo group item from API (child_menu_item_id + optional price_override). */
type ComboItemRaw = { child_menu_item_id: string; price_override?: number }

/** Combo group from API. */
type ComboGroupApi = {
  combo_group_id?: string
  group_name?: string
  is_mandatory?: boolean
  items?: ComboItemRaw[]
  min_select?: number
  max_select?: number
  display_order?: number
}

/** Optional API fields (not on MenuItemInterface) for modal-only use. Do not change backend types. */
type ApiItemLike = MenuItemInterface & {
  item_base_price?: number
  item_description?: string
  combo_groups?: ComboGroupApi[]
  modifier_groups?: ModifierGroupV2[]
}

export type ModifierUIOption = {
  id: string
  child_menu_item_id: string
  label: string
  price: number
  isDefault: boolean
  isAvailable: boolean
  /** Filled at render from selection state. */
  selected: boolean
}

export type ModifierUIGroup = {
  groupId: string
  name: string
  min: number
  max: number
  /**
   * Real backend maximum, clamped to at least `min` — captured BEFORE the
   * internal "999" unbounded-selection sentinel is applied to `max`. Used
   * only for constraint-label presentation; selection logic must keep
   * using `max`.
   */
  rawMax: number
  isSingleSelect: boolean
  options: ModifierUIOption[]
}

function mapModifierItemToUIOption(it: ModifierGroupItemV2): ModifierUIOption | null {
  const id = String(it.modifier_group_item_id)
  if (!id) return null
  const child = String(it.child_menu_item_id)
  const backendName = String(it.child_menu_item_name ?? '').trim()
  return {
    id,
    child_menu_item_id: child,
    label: backendName || 'Option',
    price: Math.max(0, Number(it.delta_price) || 0),
    isDefault: Boolean(it.is_default),
    isAvailable: it.is_effectively_selectable !== false,
    selected: false,
  }
}

/**
 * V2 `modifier_groups` → UI list (min/max, single- vs multi-select, delta price).
 * Stable ordering: by group `display_order`, then options by `display_order`.
 */
function mapModifierGroupsToModifierUI(groups: ModifierGroupV2[] | undefined): ModifierUIGroup[] {
  if (!Array.isArray(groups) || groups.length === 0) return []
  return [...groups]
    .filter((g) => Array.isArray(g?.items) && g.items.length > 0)
    .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
    .map((g, gi) => {
      const min = Math.max(0, Number(g?.min_select) ?? 0)
      const max = Math.max(min, Math.max(0, Number(g?.max_select) ?? 0))
      const maxClamped = max === 0 && min === 0 ? 999 : max
      const isSingleSelect = min === 1 && maxClamped === 1
      const groupId = String(g?.modifier_group_id ?? `mod-group-fallback-${gi}`)
      const name = (g?.display_name || g?.group_name || 'Options').trim() || 'Options'
      const rawOptions = [...(g.items ?? [])]
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((it) => mapModifierItemToUIOption(it))
        .filter((x): x is ModifierUIOption => x != null)
      if (rawOptions.length === 0) return null
      return {
        groupId,
        name,
        min,
        max: maxClamped,
        rawMax: max,
        isSingleSelect,
        options: rawOptions,
      }
    })
    .filter((x): x is ModifierUIGroup => x != null)
}

/** Finite, non-negative integer with fallback — same convention as menuV2Adapter.ts's num(). */
function safeCount(value: unknown, fallback: number): number {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, n) : fallback
}

/**
 * Single authoritative "Required"/"Optional" constraint label for a
 * modifier/combo group header, replacing the old separate
 * "Required (min N)" + "Choose N-M" pair.
 *
 * `rawMax` must be the real backend maximum (already clamped to at least
 * `min`), captured BEFORE any internal selection-cap sentinel (e.g. the
 * 999 fallback used to bound "unlimited" selection) is applied — that
 * sentinel must never reach this function or the rendered text.
 */
function formatSelectionConstraint(min: number, rawMax: number): { isRequired: boolean; text: string } {
  const safeMin = Math.floor(Math.max(0, min))
  const safeRawMax = Math.floor(Math.max(0, rawMax))
  const hasBoundedMax = safeRawMax > safeMin

  if (safeMin > 0) {
    return {
      isRequired: true,
      text: hasBoundedMax ? `Required (min ${safeMin}, max ${safeRawMax})` : `Required (min ${safeMin})`,
    }
  }
  return {
    isRequired: false,
    text: hasBoundedMax ? `Optional (max ${safeRawMax})` : 'Optional',
  }
}

function buildInitialModifierSelections(mui: ModifierUIGroup[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const g of mui) {
    const available = g.options.filter((o) => o.isAvailable)
    const defaultAvailable = available.filter((o) => o.isDefault)
    const maxAllowed = Math.max(0, g.max)
    const required = Math.max(0, Math.min(g.min, maxAllowed))

    // Single-select required groups: first default, else first available.
    if (g.isSingleSelect) {
      const first = defaultAvailable[0] ?? available[0]
      out[g.groupId] = first ? [first.id] : []
      continue
    }

    const picked: string[] = []
    const seen = new Set<string>()
    const pushUnique = (id: string) => {
      if (!id || seen.has(id) || picked.length >= maxAllowed) return
      seen.add(id)
      picked.push(id)
    }

    // Start with defaults (already display_order sorted via mapModifierGroupsToModifierUI).
    for (const o of defaultAvailable) pushUnique(o.id)

    // Ensure min_select is satisfied by filling from other available options.
    if (picked.length < required) {
      for (const o of available) {
        pushUnique(o.id)
        if (picked.length >= required) break
      }
    }

    out[g.groupId] = picked
  }
  return out
}

const IMAGE_HEIGHT = 250
const MAX_IMAGE_HEIGHT_VH = 0.4
const MIN_IMAGE_HEIGHT = 132
const COLLAPSE_RANGE = 220

function getMaxImageHeight(): number {
  if (typeof window === 'undefined') return 280
  return Math.round(window.innerHeight * MAX_IMAGE_HEIGHT_VH)
}

function normalizeSnapHeight(value: string | number, viewportHeight: number): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.endsWith('%')) {
      const n = Number(trimmed.slice(0, -1))
      if (!Number.isFinite(n)) return null
      return (n / 100) * viewportHeight
    }
    const n = Number(trimmed)
    if (Number.isFinite(n)) return n
  }
  return null
}

function MenuItemDetailModal({
  item,
  isOpen,
  onClose,
  editClientItemId = null,
  sheetSnapPoints = ['50%', '90%'],
  sheetInitialSnap = 1,
}: Props) {
  const theme = useTheme()
  const tokens = getDesignTokens(theme)
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { primaryTokens, selectionTokens } = useTenantBranding()
  const tenantAddCtaSx = primaryTokens ? tenantActionCtaSx(primaryTokens) : null
  const selection = selectionTokens ?? tokens.selection

  const contentRef = useRef<HTMLDivElement | null>(null)
  const parallaxImageRef = useRef<HTMLDivElement | null>(null)
  const maxHeaderHeight = getMaxImageHeight()
  const initialCollapseProgress = useMemo(() => {
    if (typeof window === 'undefined') return 0
    const viewportHeight = window.innerHeight
    const normalized = (sheetSnapPoints ?? [])
      .map((p) => normalizeSnapHeight(p, viewportHeight))
      .filter((n): n is number => n != null && Number.isFinite(n))
      .sort((a, b) => a - b)
    if (normalized.length < 2) return 0
    const index = Math.max(0, Math.min(sheetInitialSnap, normalized.length - 1))
    const current = normalized[index]
    const max = normalized[normalized.length - 1]
    const ratio = Math.max(0, Math.min(current / max, 1))
    // Lower snap opens with a partially collapsed header; highest snap opens near full.
    return Math.max(0, Math.min(0.85, (1 - ratio) * 0.65))
  }, [sheetInitialSnap, sheetSnapPoints])
  const dispatch = useDispatch()
  const menuItems = useSelector((state: RootState) => state.menuItems?.list ?? [])
  const isLocked = useCartStore((s) => s.isLocked)
  const isCheckoutLocked = useCartStore((s) => s.isCheckoutLocked)
  const { notifyCartMutationBlocked } = useCartLockRecovery()
  const addItemToStore = useCartStore((s) => s.addItem)
  const updateItemQuantityStore = useCartStore((s) => s.updateItemQuantity)
  const editId = String(editClientItemId ?? '').trim()
  const isEditMode = editId.length > 0
  const editLineLive = useCartStore((s) =>
    isEditMode ? s.items.find((l) => String(l.client_item_id) === editId) : undefined
  )
  const editLineMissing = isEditMode && !editLineLive

  const apiItem = item as ApiItemLike
  const description = apiItem?.description ?? apiItem?.item_description ?? ''
  const basePrice = apiItem?.item_price ?? apiItem?.item_base_price ?? 0
  const basePriceNumber = Number(basePrice)
  const safeBasePrice = Number.isFinite(basePriceNumber) ? basePriceNumber : 0

  const [quantity, setQuantity] = useState(1)
  const [selectedComboItems, setSelectedComboItems] = useState<Record<string, string[]>>({})
  /** Per V2 `modifier_group_id`: selected `modifier_group_item_id` list (multi) or one id (single). */
  const [modifierGroupSelections, setModifierGroupSelections] = useState<Record<string, string[]>>({})
  const [selectionError, setSelectionError] = useState<string | null>(null)
  /** Baseline cart qty when Edit opened / last drift-refreshed — used to block silent overwrite. */
  const [editBaselineQuantity, setEditBaselineQuantity] = useState<number | null>(null)
  const [editNotice, setEditNotice] = useState<string | null>(null)

  const comboGroups = useMemo(() => apiItem?.combo_groups ?? [], [apiItem?.combo_groups])

  const getMenuItemById = useCallback(
    (id: string) => menuItems.find((m) => m.menu_item_id === id),
    [menuItems]
  )

  const modifierSource = (item as ApiItemLike).modifier_groups
  const modifierUI = useMemo(
    () => mapModifierGroupsToModifierUI(modifierSource),
    [modifierSource]
  )

  const hasModifierSection = modifierUI.length > 0

  /** Combo groups with items normalized (full menu item + effective_price + combo_group_id). */
  const normalizedComboGroups = useMemo(() => {
    return comboGroups
      .filter((g) => Array.isArray(g?.items) && g.items.length > 0)
      .map((group, index) => {
        const groupId = group?.combo_group_id ?? `combo-${index}-${group?.group_name ?? 'group'}`
        const items = (group.items ?? []).map((comboItem) => {
          const childMenuItem = getMenuItemById(comboItem.child_menu_item_id)
          const effectivePrice =
            (comboItem.price_override !== undefined && comboItem.price_override !== null
              ? Number(comboItem.price_override)
              : null) ?? (childMenuItem ? Number((childMenuItem as { item_price?: number }).item_price) : 0) ?? 0
          return {
            ...childMenuItem,
            item_name: childMenuItem?.item_name ?? 'Item',
            description: childMenuItem?.description,
            item_price: effectivePrice,
            effective_price: effectivePrice,
            combo_group_id: groupId,
            child_menu_item_id: comboItem.child_menu_item_id,
          }
        })
        return {
          ...group,
          combo_group_id: groupId,
          items,
        }
      })
      .sort((a, b) => (Number(a?.display_order) ?? 0) - (Number(b?.display_order) ?? 0))
  }, [comboGroups, getMenuItemById])

  const hasComboGroups = normalizedComboGroups.length > 0

  /**
   * True when the item exposes customization UI (V2 modifier groups and/or combo groups).
   * Source of truth matches the same lists used to render modifier + combo sections.
   */
  const hasModifiers = hasModifierSection || hasComboGroups
  /**
   * Immersive hero image only when there is nothing to configure (keeps modifier
   * controls above the fold) AND an image actually exists — otherwise the compact
   * header chrome is used and detail content reflows to fill the space.
   */
  const showHeroImageSection = !hasModifiers && Boolean(item?.image_url)

  const { headerHeight } = useCollapsibleHeader({
    isActive: isOpen && showHeroImageSection,
    isMobile,
    contentRef,
    parallaxRef: parallaxImageRef,
    maxHeightPx: maxHeaderHeight,
    minHeightPx: MIN_IMAGE_HEIGHT,
    collapseRangePx: COLLAPSE_RANGE,
    initialCollapseProgress,
  })

  useEffect(() => {
    if (!isOpen) return
    setSelectionError(null)
    setEditNotice(null)
    if (isEditMode) {
      const line = useCartStore
        .getState()
        .items.find((l) => String(l.client_item_id) === editId)
      if (line) {
        const qty = Math.max(1, Math.floor(Number(line.quantity) || 1))
        setQuantity(qty)
        setEditBaselineQuantity(qty)
      } else {
        setQuantity(1)
        setEditBaselineQuantity(null)
        setEditNotice('This item is no longer in your cart.')
      }
      // Quantity-only Edit: do not hydrate or expose editable modifier/combo composers.
      setSelectedComboItems({})
      setModifierGroupSelections({})
      return
    }
    setQuantity(1)
    setEditBaselineQuantity(null)
    setSelectedComboItems({})
    if (modifierUI.length > 0) {
      setModifierGroupSelections(buildInitialModifierSelections(modifierUI))
    } else {
      setModifierGroupSelections({})
    }
    // Intentionally omit `modifierUI`: its identity can change when unrelated menu data refreshes; resets are keyed by item id + open state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.menu_item_id, isEditMode, editId])

  useEffect(() => {
    if (!isOpen || !isEditMode) return
    if (!editLineLive) {
      setEditNotice('This item is no longer in your cart.')
      setEditBaselineQuantity(null)
    }
  }, [isOpen, isEditMode, editLineLive])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const comboTotal = useMemo(() => {
    return Object.entries(selectedComboItems).reduce((sum, [groupId, childIds]) => {
      return (
        sum +
        childIds.reduce((childSum, childId) => {
          const group = normalizedComboGroups.find((g) => g.combo_group_id === groupId)
          const comboItem = group?.items?.find(
            (i) => (i as { child_menu_item_id?: string }).child_menu_item_id === childId
          )
          const effective =
            comboItem && 'effective_price' in comboItem
              ? Number((comboItem as { effective_price: number }).effective_price)
              : 0
          return childSum + effective
        }, 0)
      )
    }, 0)
  }, [selectedComboItems, normalizedComboGroups])

  const modifierSelectionValid = useMemo(() => {
    if (!hasModifierSection) return true
    return modifierUI.every((g) => {
      const n = (modifierGroupSelections[g.groupId] ?? []).length
      return n >= g.min && n <= g.max
    })
  }, [hasModifierSection, modifierUI, modifierGroupSelections])

  const comboSelectionValid = useMemo(() => {
    return normalizedComboGroups.every(
      (group) =>
        (Number(group?.min_select) ?? 0) <= 0 ||
        (selectedComboItems[group.combo_group_id]?.length ?? 0) >= (Number(group?.min_select) ?? 0)
    )
  }, [normalizedComboGroups, selectedComboItems])

  const selectedCombosForCart = useMemo(() => {
    return Object.entries(selectedComboItems).flatMap(([groupId, childIds]) =>
      childIds.map((childId) => {
        const group = normalizedComboGroups.find((g) => g.combo_group_id === groupId)
        const comboItem = group?.items?.find(
          (i) => (i as { child_menu_item_id?: string }).child_menu_item_id === childId
        )
        const effectivePrice =
          comboItem && 'effective_price' in comboItem
            ? Number((comboItem as { effective_price: number }).effective_price)
            : 0
        return {
          combo_group_id: groupId,
          child_menu_item_id: childId,
          effective_price: effectivePrice,
        }
      })
    )
  }, [selectedComboItems, normalizedComboGroups])

  const selectedModifierGroupsForCart = useMemo((): CartModifierGroupStored[] => {
    if (!hasModifierSection) return []
    const out: CartModifierGroupStored[] = []
    const seen = new Set<string>()
    for (const g of modifierUI) {
      const rows: CartModifierGroupStored["items"] = []
      for (const itemId of modifierGroupSelections[g.groupId] ?? []) {
        const key = `${g.groupId}:${itemId}`
        if (seen.has(key)) continue
        const opt = g.options.find((o) => o.id === itemId)
        if (opt) {
          seen.add(key)
          rows.push({
            modifier_group_item_id: opt.id,
            child_menu_item_id: opt.child_menu_item_id,
            quantity: 1,
            delta_price: opt.price,
          })
        }
      }
      if (rows.length > 0) {
        out.push({ modifier_group_id: g.groupId, items: rows })
      }
    }
    return out
  }, [hasModifierSection, modifierUI, modifierGroupSelections])

  const lineTotal = useMemo(() => {
    if (isEditMode && editLineLive) {
      const mods = cartLineToSelectedModifiers(editLineLive, quantity)
      const comboPer = Number(editLineLive.comboTotalPerUnit) || 0
      return (
        computeLineTotalForCartItemBase(Number(editLineLive.item_price) || 0, quantity, {
          selectedModifiers: mods,
        }) + quantity * comboPer
      )
    }
    const mods = hasModifierSection
      ? cartLineToSelectedModifiers({ selected_modifiers: selectedModifierGroupsForCart }, quantity)
      : undefined
    return (
      computeLineTotalForCartItemBase(safeBasePrice, quantity, {
        selectedModifiers: mods,
      }) + quantity * comboTotal
    )
  }, [
    isEditMode,
    editLineLive,
    safeBasePrice,
    quantity,
    hasModifierSection,
    selectedModifierGroupsForCart,
    comboTotal,
  ])

  const toggleComboItem = useCallback(
    (groupId: string, childId: string, maxSelect: number, isSingleSelect: boolean) => {
      setSelectedComboItems((prev) => {
        const current = prev[groupId] ?? []
        if (isSingleSelect) {
          return { ...prev, [groupId]: [childId] }
        }
        const exists = current.includes(childId)
        if (exists) {
          return {
            ...prev,
            [groupId]: current.filter((id) => id !== childId),
          }
        }
        if (current.length >= maxSelect) return prev
        return {
          ...prev,
          [groupId]: [...current, childId],
        }
      })
    },
    []
  )

  const toggleModifierOption = useCallback(
    (group: ModifierUIGroup, optionId: string) => {
      if (!optionId) return
      setModifierGroupSelections((prev) => {
        const current = prev[group.groupId] ?? []
        const option = group.options.find((o) => o.id === optionId)
        if (!option || !option.isAvailable) {
          setSelectionError('This option is currently unavailable.')
          return prev
        }
        if (group.isSingleSelect) {
          const newSelected = [optionId]
          setSelectionError(null)
          return { ...prev, [group.groupId]: newSelected }
        }
        const exists = current.includes(optionId)
        if (exists) {
          if (current.length - 1 < group.min) {
            setSelectionError(`Select at least ${group.min} option(s) in ${group.name}.`)
            return prev
          }
          const newSelected = current.filter((id) => id !== optionId)
          setSelectionError(null)
          return {
            ...prev,
            [group.groupId]: newSelected,
          }
        }
        if (current.length < group.max) {
          const newSelected = [...current, optionId]
          setSelectionError(null)
          return { ...prev, [group.groupId]: newSelected }
        }
        // Max reached: replace oldest selected option with newly clicked option.
        const replaced = [...current.slice(1), optionId]
        setSelectionError(null)
        return { ...prev, [group.groupId]: replaced }
      })
    },
    []
  )

  const cartItemsForAggregate = useCartStore(selectCartItems)
  /**
   * Aggregate quantity already committed for this menu_item_id across OTHER cart lines
   * (different modifier/combo configs included, per-line being edited excluded) — the
   * quantity being configured here must fit within MAX_MENU_ITEM_QUANTITY on top of this.
   */
  const otherLinesMenuItemQuantity = useMemo(() => {
    const menuItemId = item?.menu_item_id ? String(item.menu_item_id) : ''
    if (!menuItemId) return 0
    const others = isEditMode
      ? cartItemsForAggregate.filter((l) => l.client_item_id !== editId)
      : cartItemsForAggregate
    return sumMenuItemQuantity(others, menuItemId)
  }, [cartItemsForAggregate, item?.menu_item_id, isEditMode, editId])
  const atMenuItemMax = otherLinesMenuItemQuantity + quantity >= MAX_MENU_ITEM_QUANTITY

  const handleQuantityChange = useCallback(
    (delta: number) => {
      setQuantity((prev) => {
        const next = Math.max(1, prev + delta)
        if (delta > 0 && otherLinesMenuItemQuantity + next > MAX_MENU_ITEM_QUANTITY) return prev
        return next
      })
    },
    [otherLinesMenuItemQuantity]
  )

  const resolveEditLine = useCallback(() => {
    if (!editId) return undefined
    return readSafeCartItems().find((l) => String(l.client_item_id) === editId)
  }, [editId])

  const handleEditUpdate = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()
      if (isLocked || isCheckoutLocked) {
        notifyCartMutationBlocked()
        return
      }
      if (!isEditMode) return
      const line = resolveEditLine()
      if (!line) {
        setEditNotice('This item is no longer in your cart.')
        setEditBaselineQuantity(null)
        return
      }
      const liveQty = Math.max(0, Math.floor(Number(line.quantity) || 0))
      if (editBaselineQuantity != null && liveQty !== editBaselineQuantity) {
        const refreshed = Math.max(1, liveQty)
        setQuantity(refreshed)
        setEditBaselineQuantity(refreshed)
        setEditNotice('Cart quantity changed. Review the quantity and press Update again.')
        return
      }
      const nextQty = Math.max(1, Math.floor(Number(quantity) || 1))
      const others = readSafeCartItems().filter((l) => l.client_item_id !== editId)
      const otherAggregate = sumMenuItemQuantity(others, String(line.menu_item_id))
      if (otherAggregate + nextQty > MAX_MENU_ITEM_QUANTITY) {
        toast.error(`You can order up to ${MAX_MENU_ITEM_QUANTITY} of this item.`)
        return
      }
      updateItemQuantityStore({
        menu_item_id: String(line.menu_item_id),
        quantity: nextQty,
        selected_modifiers: line.selected_modifiers,
        selectedCombos: line.selectedCombos,
        comboTotalPerUnit: line.comboTotalPerUnit,
      })
      onClose()
    },
    [
      isLocked,
      isCheckoutLocked,
      notifyCartMutationBlocked,
      isEditMode,
      resolveEditLine,
      editBaselineQuantity,
      editId,
      quantity,
      updateItemQuantityStore,
      onClose,
    ]
  )

  const handleEditRemove = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()
      if (isLocked || isCheckoutLocked) {
        notifyCartMutationBlocked()
        return
      }
      if (!isEditMode) return
      const line = resolveEditLine()
      if (!line) {
        setEditNotice('This item is no longer in your cart.')
        setEditBaselineQuantity(null)
        return
      }
      updateItemQuantityStore({
        menu_item_id: String(line.menu_item_id),
        quantity: 0,
        selected_modifiers: line.selected_modifiers,
        selectedCombos: line.selectedCombos,
        comboTotalPerUnit: line.comboTotalPerUnit,
      })
      onClose()
    },
    [
      isLocked,
      isCheckoutLocked,
      notifyCartMutationBlocked,
      isEditMode,
      resolveEditLine,
      updateItemQuantityStore,
      onClose,
    ]
  )

  const handleAddToCart = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (isEditMode) return
    if (isLocked || isCheckoutLocked) {
      // Close details first so recovery is not stacked on top of Add.
      onClose()
      queueMicrotask(() => {
        notifyCartMutationBlocked()
      })
      return
    }
    if (!item?.menu_item_id) return
    const liveAggregate = sumMenuItemQuantity(readSafeCartItems(), String(item.menu_item_id))
    if (liveAggregate + quantity > MAX_MENU_ITEM_QUANTITY) {
      toast.error(`You can order up to ${MAX_MENU_ITEM_QUANTITY} of this item.`)
      return
    }
    addItemToStore({
      menu_item_id: item.menu_item_id,
      item_name: item.item_name,
      item_price: safeBasePrice,
      category: item.category,
      restaurant_id: item.restaurant_id,
      selected_modifiers: selectedModifierGroupsForCart,
      selectedCombos: selectedCombosForCart,
      comboTotalPerUnit: comboTotal,
    })
    if (quantity > 1) {
      updateItemQuantityStore({
        menu_item_id: item.menu_item_id,
        quantity,
        selected_modifiers: selectedModifierGroupsForCart,
        selectedCombos: selectedCombosForCart,
        comboTotalPerUnit: comboTotal,
      })
    }
    const updatedMenus = menuItems.map((el) =>
      el.menu_item_id === item.menu_item_id
        ? { ...el, count: (el?.count ?? 0) + quantity }
        : el
    )
    dispatch(updateMenuItems({ list: updatedMenus }))
    onClose()
  }, [
    isEditMode,
    item,
    quantity,
    selectedModifierGroupsForCart,
    selectedCombosForCart,
    comboTotal,
    safeBasePrice,
    isLocked,
    isCheckoutLocked,
    notifyCartMutationBlocked,
    addItemToStore,
    updateItemQuantityStore,
    dispatch,
    menuItems,
    onClose,
  ])

  if (!isOpen) return null

  const editActionsDisabled =
    isLocked || isCheckoutLocked || editLineMissing || !item?.is_available
  const itemLabel = item?.item_name?.trim() || 'item'

  const sheetCtaFooter = (
    <Box
      component="footer"
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        columnGap: designTokens.spacing.lg / 8,
        rowGap: designTokens.spacing.sm / 8,
        flexWrap: 'nowrap',
        width: '100%',
        minHeight: { xs: 56, md: 52 },
        px: { xs: designTokens.spacing.lg / 8, md: 0 },
        pt: designTokens.spacing.md / 8,
        pb: {
          xs: `calc(env(safe-area-inset-bottom, 0px) + ${designTokens.spacing.md}px)`,
          md: `calc(env(safe-area-inset-bottom, 0px) + ${designTokens.spacing.lg}px)`,
        },
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.88 : 0.92),
        backdropFilter: 'saturate(180%) blur(14px)',
        WebkitBackdropFilter: 'saturate(180%) blur(14px)',
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 -10px 28px rgba(0,0,0,0.42)'
            : '0 -8px 24px rgba(15, 23, 42, 0.08)',
        zIndex: 2,
        isolation: 'isolate',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: designTokens.spacing.lg / 8,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            ...getMenuQuantityStepperShellSx(theme, 'modal'),
            opacity: isEditMode && editLineMissing ? 0.5 : 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => handleQuantityChange(-1)}
            disabled={isEditMode && editActionsDisabled}
            aria-label={
              isEditMode
                ? `Decrease quantity of ${itemLabel}`
                : undefined
            }
            sx={getMenuQuantityStepperButtonSx(theme, 'modal')}
          >
            −
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={getMenuQuantityStepperQtySx(theme, 'modal')}
          >
            {quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleQuantityChange(1)}
            disabled={(isEditMode && editActionsDisabled) || atMenuItemMax}
            aria-label={
              isEditMode
                ? `Increase quantity of ${itemLabel}`
                : undefined
            }
            sx={getMenuQuantityStepperButtonSx(theme, 'modal')}
          >
            +
          </IconButton>
        </Box>
        <Typography
          variant="h5"
          sx={{
            minWidth: 0,
            fontWeight: fontWeight.bold,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {formatMoney(lineTotal)}
        </Typography>
      </Box>
      {isEditMode ? (
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <PrimaryButton
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleEditRemove(e)
            }}
            disabled={editActionsDisabled}
            aria-label={`Remove ${itemLabel} from cart`}
            size="medium"
            sx={{
              minWidth: 88,
              flexShrink: 0,
              alignSelf: 'center',
              borderRadius: `${designTokens.shape.borderRadius.lg}px`,
              background: 'transparent',
              color: theme.palette.text.secondary,
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                background: alpha(theme.palette.error.main, 0.06),
                color: theme.palette.error.main,
                boxShadow: 'none',
                filter: 'none',
              },
              ...(isDesktop && {
                height: 39,
                px: designTokens.spacing.md / 8,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.fontWeightMedium,
              }),
            }}
          >
            Remove
          </PrimaryButton>
          <PrimaryButton
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleEditUpdate(e)
            }}
            disabled={editActionsDisabled}
            aria-label={`Update quantity of ${itemLabel}`}
            data-tenant-primary={primaryTokens?.fill}
            size="medium"
            sx={[
              {
                minWidth: 110,
                flexShrink: 0,
                alignSelf: 'center',
                borderRadius: `${designTokens.shape.borderRadius.lg}px`,
                ...(isDesktop && {
                  height: 39,
                  px: designTokens.spacing.lg / 8,
                  fontSize: theme.typography.body2.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                }),
              },
              ...(tenantAddCtaSx ? [tenantAddCtaSx] : []),
            ]}
          >
            Update
          </PrimaryButton>
        </Box>
      ) : (
        <PrimaryButton
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleAddToCart(e)
          }}
          disabled={!item?.is_available || !comboSelectionValid || !modifierSelectionValid}
          size="medium"
          data-tenant-primary={primaryTokens?.fill}
          sx={[
            {
              minWidth: 110,
              flexShrink: 0,
              alignSelf: 'center',
              borderRadius: `${designTokens.shape.borderRadius.lg}px`,
              ml: 'auto',
              ...(isDesktop && {
                height: 39,
                px: designTokens.spacing.lg / 8,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.fontWeightMedium,
              }),
            },
            ...(tenantAddCtaSx ? [tenantAddCtaSx] : []),
          ]}
        >
          Add
        </PrimaryButton>
      )}
    </Box>
  )

  const scrollableDetailBody = (
    <ScrollFadeContainer
      scrollRef={contentRef}
      aria-label="Menu item details"
      scrollSx={{
        p: designTokens.spacing.lg / 8,
        pb: { xs: designTokens.spacing.md / 8, md: designTokens.spacing.xxl / 8 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: designTokens.spacing.md / 8,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: designTokens.spacing.md / 8 }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            m: 0,
            color: theme.palette.text.primary,
            wordBreak: 'break-word',
          }}
        >
          {item?.item_name ?? ''}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <PriceDisplay
          pricingProjection={item.pricing_projection ?? null}
          promotion={item.promotion ?? null}
          legacyItemPrice={item.item_price}
          size="default"
        />
      </Box>

      {editNotice ? (
        <Typography
          role="status"
          data-testid="menu-item-edit-notice"
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.35,
            px: 1,
            py: 0.75,
            borderRadius: `${designTokens.shape.borderRadius.sm}px`,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
          }}
        >
          {editNotice}
        </Typography>
      ) : null}

      {description ? <ExpandableDescription text={description} /> : null}

      <MenuItemMetadata tags={item?.tags} />

      {!isEditMode && (hasModifierSection || hasComboGroups) && (
        <Box
          sx={{
            height: '1px',
            mt: designTokens.spacing.lg / 8,
            background: `linear-gradient(to right, transparent, ${alpha(theme.palette.text.primary, 0.08)}, transparent)`,
          }}
        />
      )}

      {!isEditMode && hasModifierSection && (
        <Box component="section" aria-label="Modifier options" sx={{ mt: designTokens.spacing.sm / 8 }}>
          <Box
            sx={{
              height: '1px',
              mb: designTokens.spacing.md / 8,
              background: alpha(theme.palette.text.primary, 0.06),
            }}
          />
          {modifierUI.map((group, gi) => {
            const constraint = formatSelectionConstraint(group.min, group.rawMax)
            return (
              <Box
                key={buildStableKey(REACT_STABLE_KEY_TYPE.modifierGroup, gi, group.name, group.min, group.max)}
                sx={{ mb: { xs: designTokens.spacing.lg / 8, md: designTokens.spacing.xxl / 8 } }}
              >
                <Typography
                  component="h3"
                  variant="subtitle1"
                  sx={{
                    m: 0,
                    fontWeight: fontWeight.medium,
                    color: theme.palette.text.primary,
                    mb: designTokens.spacing.sm / 8,
                  }}
                >
                  {group.name}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      color: constraint.isRequired ? theme.palette.warning.main : theme.palette.text.secondary,
                    }}
                  >
                    {constraint.text}
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {group.options.map((opt, oi) => {
                    const checked = (modifierGroupSelections[group.groupId] ?? []).includes(opt.id)
                    const disabled = !opt.isAvailable
                    return (
                      <Box
                        key={buildStableKey(
                          REACT_STABLE_KEY_TYPE.modifierOption,
                          oi,
                          gi,
                          opt.label,
                          normalizeNumber(opt.price)
                        )}
                        component="label"
                        data-tenant-selection={checked ? selectionTokens?.text : undefined}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          py: designTokens.spacing.md / 8,
                          px: designTokens.spacing.md / 8,
                          borderRadius: `${designTokens.shape.borderRadius.sm}px`,
                          border: `1px solid ${checked ? selection.borderStrong : theme.palette.divider}`,
                          backgroundColor: checked
                            ? selection.surfaceTinted
                            : alpha(theme.palette.text.primary, 0.02),
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.5 : 1,
                          transition: `background ${designTokens.motion.normal}, border-color ${designTokens.motion.normal}`,
                          ...(!disabled && !checked && {
                            '&:hover': { backgroundColor: selection.hoverSurface },
                          }),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          <input
                            type={group.isSingleSelect ? 'radio' : 'checkbox'}
                            name={group.isSingleSelect ? group.groupId : undefined}
                            checked={checked}
                            onChange={() => toggleModifierOption(group, opt.id)}
                            disabled={disabled}
                            style={{ accentColor: selection.ring, flexShrink: 0 }}
                          />
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ color: theme.palette.text.primary, minWidth: 0 }}
                          >
                            {opt.label}
                          </Typography>
                          {disabled && (
                            <Box sx={{ flexShrink: 0 }}>
                              <TagChip label="Unavailable" variant="outline" size="compact" interactive={false} />
                            </Box>
                          )}
                        </Box>
                        {opt.price > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: fontWeight.semibold,
                              color: theme.palette.text.primary,
                              flexShrink: 0,
                              minWidth: 72,
                              textAlign: 'right',
                            }}
                          >
                            +{formatMoney(opt.price)}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
          {selectionError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: theme.palette.error.main }}>
              {selectionError}
            </Typography>
          )}
        </Box>
      )}

      {!isEditMode && hasComboGroups && (
        <Box component="section" aria-label="Combo options" sx={{ mt: designTokens.spacing.sm / 8 }}>
          <Box
            sx={{
              height: '1px',
              mb: designTokens.spacing.md / 8,
              background: alpha(theme.palette.text.primary, 0.06),
            }}
          />
          {normalizedComboGroups.map((group, gIdx) => {
            const groupId = group.combo_group_id
            // Selection enforcement is intentionally untouched (pre-existing formula) — see audit
            // note: no confirmed backend contract establishes max_select=0/absent as "unbounded"
            // for combo groups, so selection behavior is not changed by the header-label cleanup.
            const minSelect = Math.max(0, Number(group?.min_select) ?? 0)
            const maxSelect = Math.max(0, Number(group?.max_select) ?? 999)
            const isSingleSelect = minSelect === 1 && maxSelect === 1
            const selectedIds = selectedComboItems[groupId] ?? []
            const atMax = selectedIds.length >= maxSelect
            // Display-only, NaN-safe normalization for the header label — does not feed atMax/enforcement.
            const displayMin = safeCount(group?.min_select, 0)
            const displayMax = safeCount(group?.max_select, 0)

            return (
              <Box
                key={buildStableKey(
                  REACT_STABLE_KEY_TYPE.comboGroup,
                  gIdx,
                  group?.group_name,
                  minSelect,
                  maxSelect
                )}
                sx={{ mb: { xs: designTokens.spacing.lg / 8, md: designTokens.spacing.xxl / 8 } }}
              >
                <Typography
                  component="h3"
                  variant="subtitle1"
                  sx={{
                    m: 0,
                    fontWeight: fontWeight.medium,
                    color: theme.palette.text.primary,
                    mb: designTokens.spacing.sm / 8,
                  }}
                >
                  {group?.group_name ?? 'Choose option'}
                  {(() => {
                    const constraint = formatSelectionConstraint(displayMin, displayMax)
                    return (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          color: constraint.isRequired ? theme.palette.warning.main : theme.palette.text.secondary,
                        }}
                      >
                        {constraint.text}
                      </Typography>
                    )
                  })()}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {(group.items ?? []).map((comboItem, cIdx) => {
                    const childId = (comboItem as { child_menu_item_id: string }).child_menu_item_id
                    const effectivePrice = Number((comboItem as { effective_price?: number }).effective_price) || 0
                    const name = (comboItem as { item_name?: string }).item_name ?? 'Item'
                    const desc = (comboItem as { description?: string }).description
                    const checked = isSingleSelect ? selectedIds[0] === childId : selectedIds.includes(childId)
                    const disabled = !checked && atMax

                    return (
                      <Box
                        key={buildStableKey(
                          REACT_STABLE_KEY_TYPE.comboOption,
                          cIdx,
                          gIdx,
                          name,
                          normalizeNumber(effectivePrice)
                        )}
                        component="label"
                        data-tenant-selection={checked ? selectionTokens?.text : undefined}
                        onClick={() => !disabled && toggleComboItem(groupId, childId, maxSelect, isSingleSelect)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          py: designTokens.spacing.md / 8,
                          px: designTokens.spacing.md / 8,
                          borderRadius: `${designTokens.shape.borderRadius.md}px`,
                          border: `1px solid ${checked ? selection.border : theme.palette.divider}`,
                          backgroundColor: checked ? selection.surfaceSubtle : 'transparent',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.6 : 1,
                          ...(!disabled && !checked && {
                            '&:hover': { backgroundColor: selection.hoverSurface },
                          }),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <input
                            type={isSingleSelect ? 'radio' : 'checkbox'}
                            name={isSingleSelect ? groupId : undefined}
                            checked={checked}
                            onChange={() => {}}
                            disabled={disabled}
                            style={{ accentColor: selection.ring }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                              {name}
                            </Typography>
                            {desc && (
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
                                {desc}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {effectivePrice > 0 && (
                          <Typography variant="body2" sx={{ fontWeight: fontWeight.semibold, color: theme.palette.text.primary, flexShrink: 0 }}>
                            +{formatMoney(effectivePrice)}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
      </Box>
    </ScrollFadeContainer>
  )

  /** Close control when the hero image column is hidden — same action as the overlay on the hero. */
  const compactHeaderChrome = showHeroImageSection ? null : (
    <Box
      sx={{
        flexShrink: 0,
        px: designTokens.spacing.lg / 8,
        pt: 0.5,
        pb: designTokens.spacing.sm / 8,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minHeight: 44,
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          width: 40,
          height: 40,
          borderRadius: `${designTokens.shape.borderRadius.lg}px`,
          backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.06),
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.1),
          },
        }}
      >
        <CrossIcon />
      </IconButton>
    </Box>
  )

  const modalContent = (
    <SheetOverlay open={isOpen} onBackdropClick={onClose}>
      {/* Full-bleed positioning only: pointer-events none so backdrop receives taps outside the sheet */}
      <Box
        component="div"
        sx={
          isDesktop
            ? {
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                pointerEvents: 'none',
              }
            : {
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pointerEvents: 'none',
              }
        }
      >
        <Box
          className={isDesktop ? undefined : 'menu-sheet-mobile'}
          onClick={(e) => e.stopPropagation()}
          sx={
            isDesktop
              ? {
                  position: 'relative',
                  pointerEvents: 'auto',
                  width: '100%',
                  maxWidth: 820,
                  maxHeight: '85vh',
                  borderRadius: designTokens.shape.borderRadius.modal,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: designTokens.shadows.floating,
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'premiumFadeIn 240ms cubic-bezier(0.22, 1, 0.36, 1)',
                  '@keyframes premiumFadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px) scale(0.985)' },
                    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40%',
                    borderTopLeftRadius: designTokens.shape.borderRadius.modal,
                    borderTopRightRadius: designTokens.shape.borderRadius.modal,
                    background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.04 : 0.08)}, transparent)`,
                    pointerEvents: 'none',
                    zIndex: 0,
                  },
                }
              : {
                  position: 'relative',
                  pointerEvents: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  borderRadius: `${designTokens.shape.borderRadius.xl}px ${designTokens.shape.borderRadius.xl}px 0 0`,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[24],
                  display: 'flex',
                  flexDirection: 'column',
                  transform: 'translateY(0)',
                  animation: 'sheetSlideUp 220ms ease-out',
                  '@keyframes sheetSlideUp': {
                    from: { transform: 'translateY(100%)' },
                    to: { transform: 'translateY(0)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }
          }
        >
          <Box
            sx={{
              width: 48,
              height: 6,
              borderRadius: '9999px',
              backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.3 : 0.2),
              mx: 'auto',
              my: 1.5,
              opacity: 0.5,
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              ...(isDesktop
                ? {
                    display: 'grid',
                    gridTemplateColumns: showHeroImageSection ? '45% 55%' : '1fr',
                    height: '100%',
                  }
                : {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }),
            }}
          >
            {showHeroImageSection && (
              <>
                {/* Image section — desktop left column; mobile collapsible hero */}
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    width: { xs: '100%' },
                    height: isMobile ? headerHeight : '100%',
                    flexShrink: 0,
                    overflow: 'hidden',
                    ...(isDesktop && {
                      borderTopLeftRadius: designTokens.shape.borderRadius.modal,
                      borderBottomLeftRadius: designTokens.shape.borderRadius.modal,
                    }),
                    ...(isMobile && {
                      transition: `height ${designTokens.motion.fast} ease-out`,
                      willChange: 'height',
                    }),
                  }}
                >
                  {/* Top gradient fade over image */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 48,
                      background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.2)}, transparent)`,
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                  {/* Bottom gradient overlay - mobile: image-to-content transition; desktop: subtle depth */}
                  {item?.image_url && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: isDesktop ? '30%' : 80,
                        background: isDesktop
                          ? `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.common.black, 0.25)} 100%)`
                          : `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.4)}, ${alpha(theme.palette.common.black, 0)})`,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    />
                  )}
                  {/* Vertical fade between image & content - desktop only */}
                  {isDesktop && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        width: 80,
                        pointerEvents: 'none',
                        zIndex: 2,
                        background: `linear-gradient(to right, transparent, ${alpha(theme.palette.background.paper, 0.85)})`,
                      }}
                    />
                  )}
                  {/* showHeroImageSection already guarantees item.image_url is truthy here. */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      ...(isDesktop && {
                        borderRadius: 0,
                        borderTopLeftRadius: designTokens.shape.borderRadius.modal,
                        borderBottomLeftRadius: designTokens.shape.borderRadius.modal,
                        '& > img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          transition: `transform ${designTokens.motion.slow}`,
                          transformOrigin: 'center center',
                        },
                        '&:hover > img': {
                          transform: 'scale(1.09)',
                        },
                      }),
                    }}
                  >
                    <Box
                      ref={parallaxImageRef}
                      sx={{
                        width: '100%',
                        height: '100%',
                        willChange: 'transform',
                        transform: 'translate3d(0, 0px, 0)',
                      }}
                    >
                      <AppImage
                        src={item?.image_url ?? ''}
                        baseUrl={serverUrl}
                        alt={item?.item_name ?? ''}
                        width={600}
                        height={IMAGE_HEIGHT * 2}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Box>
                  <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 10,
                      width: 36,
                      height: 36,
                      borderRadius: designTokens.shape.borderRadius.lg,
                      backgroundColor: alpha(theme.palette.common.black, 0.5),
                      color: theme.palette.common.white,
                      backdropFilter: 'blur(6px)',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.common.black, 0.7),
                      },
                    }}
                  >
                    <CrossIcon />
                  </IconButton>
                </Box>
              </>
            )}

            {isDesktop ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  ...(showHeroImageSection
                    ? {
                        borderTopRightRadius: designTokens.shape.borderRadius.modal,
                        borderBottomRightRadius: designTokens.shape.borderRadius.modal,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                      }
                    : {
                        borderRadius: designTokens.shape.borderRadius.modal,
                      }),
                }}
              >
                {compactHeaderChrome}
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                    pl: designTokens.spacing.xl / 8,
                    pr: designTokens.spacing.xxxl / 8,
                  }}
                >
                  {scrollableDetailBody}
                  {sheetCtaFooter}
                </Box>
              </Box>
            ) : (
              <>
                {compactHeaderChrome}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper,
                    pl: designTokens.spacing.lg / 8,
                    pr: designTokens.spacing.lg / 8,
                  }}
                >
                  {scrollableDetailBody}
                </Box>
                {sheetCtaFooter}
              </>
            )}
          </Box>
      </Box>
      </Box>
    </SheetOverlay>
  )

  return createPortal(modalContent, document.body)
}

export default MenuItemDetailModal
